import { Scope } from '@developerz/domain';
import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { useScopeVerdict } from '../lib/scope-gate';
import { clearRepoPrompt, setRepoPrompt, useRepoPrompt } from '../lib/system-prompts';
import { usePromptLayer } from '../lib/use-prompt-editor';
import { QueryBoundary } from './QueryBoundary';
import { Skeleton } from './Skeleton';
import { SystemPromptEditor } from './SystemPromptEditor';

// REPO layer of the system-prompt cascade (#637), mounted on the repo Settings
// tab. CONTAINER: the same `<SystemPromptEditor/>` the ORG + USER layers render
// on the Prompts route — role picker included (plan 101 slice 06) — over this
// repo's role-keyed layer. It hand-copied the editor's markup until the picker
// arrived; a second copy would have been a second picker to keep in step.
//
// The repo layer is `write:prompts`, GET included: a caller whose `/v1/me`
// lacks it sends no read and the card is not rendered (#7426) — the org layer
// on the Prompts page says why; the Settings tab has nothing for them to edit.
export function RepoPromptCard(props: { repoId: string; token: string | null }): JSX.Element {
  const verdict = useScopeVerdict(Scope.WritePrompts, props.token);
  const layer = usePromptLayer({
    read: (role) =>
      useRepoPrompt(
        () => props.repoId,
        role,
        props.token,
        () => verdict() === 'allowed',
      ),
    save: (text, role) => setRepoPrompt(props.repoId, text, role, props.token),
    clear: (role) => clearRepoPrompt(props.repoId, role, props.token),
  });

  return (
    <Show when={verdict() !== 'denied'}>
      <QueryBoundary query={layer.query} loading={<Skeleton class="skeleton--card" />}>
        {() => (
          <SystemPromptEditor
            id="repo-prompt"
            title={t('system_prompts.repo_title')}
            description={t('system_prompts.repo_description')}
            value={layer.editor.value()}
            draft={layer.editor.draft()}
            onDraft={layer.editor.onDraft}
            role={layer.editor.role()}
            onRoleChange={layer.editor.onRoleChange}
            busy={layer.editor.busy()}
            error={layer.editor.error()}
            savedText={layer.editor.savedText()}
            onSave={layer.editor.onSave}
            onClear={layer.editor.onClear}
          />
        )}
      </QueryBoundary>
    </Show>
  );
}
